import { useScrollPosition } from '@n8tb1t/use-scroll-position'
import clsx from 'clsx'
import map from 'lodash/map'
import React, { useCallback, useEffect, useMemo, useState, VFC } from 'react'
import styled from 'styled-components'

import { WistiaVideoContainer } from '@Components/CMS/Video/WistiaVideoContainer'
import { up } from '@Globals/Style/breakpoints'
import {
  box,
  flexCenterChildren,
  generalBottomSpacing,
  imageLoadingCircle,
  linkStyling,
  mobilePadding,
} from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'

export const Loading = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  ${box({ width: '100%' })}
  z-index: 1;
  background-color: ${({ theme }) => theme.colors.greyLightest};

  ${flexCenterChildren}
  &:after {
    ${imageLoadingCircle({ size: '100px' })}
  }
`

export interface WistiaVideoPlayer {
  bind: (name: string, method: any) => void
  container: HTMLIFrameElement
  pause: () => void
  play: () => void
  remove: () => void
  src: string
  time: (from: number) => void
  unbind: (name: string, method: any) => void
  videoHeight: (num: any, options: any) => void
}

interface WistiaVideoProps {
  autoplay: boolean
  className?: string
  onReady?: (player: WistiaVideoPlayer) => void
  options?: Record<string, any>
  videoId: string
}

export const WistiaVideo: VFC<WistiaVideoProps> = ({
  autoplay,
  className,
  onReady,
  options,
  videoId,
}: WistiaVideoProps) => {
  const [videoLoaded, setVideoLoaded] = useState(false)
  const videoRef = React.useRef<WistiaVideoPlayer>()

  const embed = useMemo(() => ({
    id: videoId,
    onReady(player: WistiaVideoPlayer) {
      setVideoLoaded(true)
      videoRef.current = player
      if (onReady) {
        onReady(player)
      }
    },
    options,
  }), [options, videoId, onReady])

  useEffect(() => {
    const win = window as any
    // eslint-disable-next-line no-underscore-dangle
    win._wq = win._wq || []
    // eslint-disable-next-line no-underscore-dangle
    win._wq.push(embed)
  }, [embed])

  useEffect(() => {
    if (autoplay && videoLoaded) {
      (videoRef as any).current.play()
    }
  }, [autoplay, videoLoaded])

  useEffect(() => {
    const wistiaScriptID = 'th-wistia-v1'
    const hasLoadedScript = !!document.getElementById(wistiaScriptID)

    if (hasLoadedScript) {
      return
    }

    const wistiaScript = document.createElement('script')
    wistiaScript.src = 'https://fast.wistia.com/assets/external/E-v1.js'
    wistiaScript.id = wistiaScriptID
    wistiaScript.async = true
    document.body.appendChild(wistiaScript)

    // eslint-disable-next-line consistent-return
    return () => {
      // clean up wistia
      const win = window as any
      // eslint-disable-next-line no-underscore-dangle
      win._wq = win._wq || []
      // eslint-disable-next-line no-underscore-dangle
      win._wq.push({ revoke: embed })
      videoRef.current?.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <WistiaVideoContainer classes={className} videoId={videoId} videoLoaded={videoLoaded} />
  )
}

interface VideoPlayerProps {
  autoplay: boolean
  hasHeightAuto: boolean
  videoId: string
  videoType: 'wistia' | 'youtube'
}

const wistiaConfiguration = (autoPlay: boolean) => ({
  autoPlay,
  controlsVisibleOnLoad: !autoPlay,
  endVideoBehavior: autoPlay ? 'loop' : 'reset',
  fullscreenButton: true,
  muted: autoPlay,
  playButton: !autoPlay,
  playbackRateControl: false,
  playbar: !autoPlay,
  settingsControl: !autoPlay,
  smallPlayButton: !autoPlay,
  volume: autoPlay ? 0 : 100,
  volumeControl: !autoPlay,
  videoFoam: true,
})

const VideoPlayer = ({
  autoplay = false,
  hasHeightAuto,
  videoId,
  videoType,
}: VideoPlayerProps) => (
  <div className="videoWrapInner">
    {videoType === 'wistia'
      && (
        <WistiaVideo
          autoplay={autoplay}
          options={wistiaConfiguration(autoplay)}
          videoId={videoId}
        />
      )}

    {videoType === 'youtube' && (
      <div className={clsx(hasHeightAuto && 'innerPadding')}>
        <iframe
          allowFullScreen
          frameBorder="0"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}`}
          title={videoId}
        />
      </div>
    )}
  </div>
)

const ProductNames = styled.p`
  padding-right: 20px;
  padding-bottom: 5px;
  padding-left: 20px;
  color: ${({ theme }) => theme.colors.grey};
  font-size: 12px;
  ${mobilePadding}
`

const ProductsCaption = ({ products }: { products: { link: string, title: string }[] }) => {
  const { t } = useTranslation()

  if (products?.length === 0) {
    return null
  }

  return (
    <ProductNames>
      {t('cms:CMS_IMAGE_MODULE_SHOWING_LABEL')}
      {' '}
      {map(products, (item, index) => {
        let seperator = ''
        // Output: Product A, Product B, Product C & Product D
        if (index < products.length - 2) {
          seperator = ', '
        } else if (index < products.length - 1) {
          seperator = ' & '
        }

        return (
          <React.Fragment key={index}>
            <a href={item.link}>{item.title}</a>
            {seperator}
          </React.Fragment>
        )
      })}
    </ProductNames>
  )
}

const widthMap = {
  column: 'column',
  full: 'fullWidth',
  'full-screen': 'fullScreen',
  'fill-container': 'fillContainer',
}

export interface VideoProps {
  autoPlay?: boolean
  blockIndex: number
  caption?: string
  /** In case of video being cut: Used to decide what part of the video to show  */
  desktopCropping: 'top' | 'center' | 'bottom'
  /** Used to set a default height when loading - value is 'auto' or 10,20,...,100  */
  desktopMaxHeight: string
  fillContainer?: boolean
  id?: string
  products?: { link: string, title: string }[]
  type: 'full-screen' | 'full' | 'column' | 'fill-container'
  videoId: string
  videoMobileId?: string
  videoType: 'wistia' | 'youtube'
}

export const VideoWrapper = styled.div`
  ${linkStyling};
  position: relative;
  width: 100%;

  ${generalBottomSpacing}
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .wistiaVideo {
    width: 100%;
    height: 100%;
  }

  .innerPadding {
    position: relative;
    padding-bottom: 56.25%; /* 16:9 */
  }

  .videoWrapOuter {
    background-color: ${({ theme }) => theme.colors.greyLightest};

    .wistiaResponsivePadding {
      position: relative;
    }
  }

  .desktop {
    display: none !important;

    ${up('tablet')} {
      display: block !important;
    }
  }

  .mobile {
    ${up('tablet')} {
      display: none !important;
    }
  }

  &.column {
    max-width: ${({ theme }) => theme.cmsModule.width.small};
  }

  &.fullWidth {
    max-width: ${({ theme }) => theme.cmsModule.width.wide};
    width: 100%;

    ${ProductNames},
    .imageCaptions {
      margin: 0 auto;
      max-width: ${({ theme }) => theme.cmsModule.width.small};
    }
  }

  &.fillContainer, .fullScreen {
    .videoWrapOuter {
      position: relative;
      overflow: hidden;

      &.top .videoWrapInner {
        position: absolute;
        top: 0;
        left: 0;
      }

      &.bottom .videoWrapInner {
        position: absolute;
        bottom: 0;
        left: 0;
      }

      &.center {
        ${flexCenterChildren}
      }
    }
  }

  &.fillContainer {
    .wistiaVideo,
    .wistiaResponsivePadding,
    .wistia_responsive_wrapper,
    .wistia_embed,
    .wistia_embed > div,
    .wistia_embed > div > div {
      width: 100% !important;
      height: 100% !important;
    }

    .videoWrapOuter {
      .videoWrapInner {
        width: 100%;
        height: 100%;
      }
    }
  }

  &.fullScreen {
    .videoWrapOuter {
      .videoWrapInner {
        width: 100%;
        height: 100%;
      }
    }

    ${ProductNames},
    .imageCaption {
      margin: 0 auto;
      max-width: ${({ theme }) => theme.cmsModule.width.small};
    }
  }
`

const VideoInfo = styled.div`
  margin-top: -1px;
  padding-top: 5px;
  width: 100%;
  text-align: center;
  font-size: 15px;

  &.withCaption {
    background-color: ${({ theme }) => theme.colors.greyLightest};
  }

  .videoCaption {
    padding-top: 5px;
    color: ${({ theme }) => theme.colors.grey};
    ${mobilePadding}
  }
`

export const Video: VFC<VideoProps> = ({
  autoPlay = false,
  blockIndex,
  caption,
  desktopCropping,
  desktopMaxHeight = 'auto',
  fillContainer = false,
  id,
  products,
  type,
  videoId,
  videoMobileId,
  videoType,
}) => {
  const videoRef = React.useRef<any>(null)
  const [loadVideo, setLoadVideo] = useState<boolean>(false)
  const [autoPlayVideo, setAutoPlayVideo] = useState<boolean>(autoPlay)

  const checkScrollAndLoadVideoOrAutoPlayVideo = useCallback(() => {
    if (!videoRef.current) {
      return
    }

    const windowBottomPos = window.innerHeight + window.pageYOffset
    const elementRect = videoRef.current.getBoundingClientRect()

    if (autoPlay && windowBottomPos + 500 > elementRect.top && !autoPlayVideo) {
      setAutoPlayVideo(true)
      return
    }

    if (windowBottomPos + 1500 > elementRect.top && !loadVideo) {
      setLoadVideo(true)
    }
  }, [autoPlay, autoPlayVideo, loadVideo])

  useScrollPosition(
    () => {
      if (blockIndex < 3) {
        return
      }

      checkScrollAndLoadVideoOrAutoPlayVideo()
    },
    [],
    videoRef,
    undefined,
    250,
  )

  useEffect(() => {
    if (autoPlay && blockIndex < 3) {
      setLoadVideo(true)
      setAutoPlayVideo(true)
    }
  }, [autoPlay, blockIndex])

  useEffect(() => {
    checkScrollAndLoadVideoOrAutoPlayVideo()
  }, [checkScrollAndLoadVideoOrAutoPlayVideo])

  const videoWidthClass = widthMap[type]
  const hasFullScreenCustomHeight = type === 'full-screen' && desktopMaxHeight !== 'auto'
  const useBothDesktopAndMobile = videoId && videoMobileId && videoId !== videoMobileId

  let heightObj: React.CSSProperties = {}

  if (hasFullScreenCustomHeight) {
    heightObj = {
      height: `${desktopMaxHeight}vw`,
      maxHeight: `${desktopMaxHeight}vw`,
    }
  } else if (fillContainer) {
    heightObj = {
      height: '100%',
      maxHeight: '100%',
    }
  }

  return (
    <VideoWrapper className={clsx(videoWidthClass)} id={id} ref={videoRef}>
      {/* Desktop video */}
      <div
        className={clsx({
          desktop: useBothDesktopAndMobile,
          [desktopCropping]: hasFullScreenCustomHeight || fillContainer,
          videoWrapOuter: true,
        })}
        data-video-id={videoId}
        style={heightObj}
      >
        {loadVideo && (
          <VideoPlayer
            autoplay={autoPlayVideo}
            hasHeightAuto={desktopMaxHeight === 'auto'}
            videoId={videoId}
            videoType={videoType}
          />
        )}
      </div>

      {/* Has mobile video */}
      {useBothDesktopAndMobile && (
        <div className="videoWrapOuter mobile" data-video-id={videoMobileId}>
          {loadVideo && (
            <VideoPlayer
              autoplay={autoPlayVideo}
              hasHeightAuto={desktopMaxHeight === 'auto'}
              videoId={videoMobileId}
              videoType={videoType}
            />
          )}
        </div>
      )}

      {/* Video product && caption */}
      {(caption || (products?.length || 0) > 0) && (
        <VideoInfo className={clsx(caption && 'withCaption')}>
          <ProductsCaption products={products || []} />
          {caption && <p className="videoCaption">{caption}</p>}
        </VideoInfo>
      )}
    </VideoWrapper>
  )
}
